export type IsIntegratedResult =
    | { integrated: false; type: ''; version: '' }
    | { integrated: true; type: 'unknown'; version: 'unknown' }
    | {
          integrated: true;
          type:
              | 'cross-window-timeout'
              | 'cross-window-unknown'
              | 'cross-window-embed'
              | 'cross-window-popup';
          version: string;
      };

interface CrossWindowMessaging {
    connecting: PromiseLike<unknown>;
    isConnected: boolean;
    client: unknown;
}

export async function isIntegrated(
    crossWindowMessaging?: CrossWindowMessaging
): Promise<IsIntegratedResult> {
    try {
        if (window.self === window.top) {
            return {
                integrated: false,
                type: '',
                version: '',
            };
        }

        if (!crossWindowMessaging) {
            return {
                integrated: true,
                type: 'unknown',
                version: 'unknown',
            };
        }

        await Promise.race([
            crossWindowMessaging.connecting,
            new Promise(resolve => {
                window.setTimeout(resolve, 10_000);
            }),
        ]);

        if (!crossWindowMessaging.isConnected) {
            return {
                integrated: true,
                type: 'cross-window-timeout',
                version: 'unknown',
            };
        }

        if (!isCrossWindowClientWithKnownType(crossWindowMessaging)) {
            return {
                integrated: true,
                type: 'cross-window-unknown',
                version: 'unknown',
            };
        }

        return {
            integrated: true,
            type: `cross-window-${crossWindowMessaging.client.type}`,
            version: isCrossWindowClientWithIntegratorVersion(
                crossWindowMessaging
            )
                ? crossWindowMessaging.client.integratorVersion
                : 'legacy',
        };
    } catch (e) {
        /* No-op */
    }

    return {
        integrated: true,
        type: 'unknown',
        version: 'unknown',
    };
}

function isCrossWindowClientWithKnownType(
    value: CrossWindowMessaging
): value is CrossWindowMessaging & { client: { type: 'embed' | 'popup' } } {
    return (
        !!value &&
        !!value.client &&
        typeof value.client === 'object' &&
        'type' in value.client &&
        typeof value.client.type === 'string' &&
        !!value.client.type &&
        ['embed', 'popup'].includes(value.client.type)
    );
}

function isCrossWindowClientWithIntegratorVersion(
    value: CrossWindowMessaging
): value is CrossWindowMessaging & { client: { integratorVersion: string } } {
    return (
        !!value &&
        !!value.client &&
        typeof value.client === 'object' &&
        'integratorVersion' in value.client &&
        typeof value.client.integratorVersion === 'string' &&
        !!value.client.integratorVersion
    );
}
